import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, CheckCircle2, XCircle, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/services/apiClient';
import { toast } from 'sonner';

const TYPE_ICON = {
    trial_expiring_soon: { Icon: AlertTriangle, cls: 'text-amber-500', bg: 'bg-amber-500/10' },
    grace_period_started: { Icon: AlertTriangle, cls: 'text-orange-500', bg: 'bg-orange-500/10' },
    subscription_expired: { Icon: XCircle, cls: 'text-destructive', bg: 'bg-destructive/10' },
    upgrade_requested: { Icon: TrendingUp, cls: 'text-primary', bg: 'bg-primary/10' },
    plan_upgraded: { Icon: CheckCircle2, cls: 'text-green-500', bg: 'bg-green-500/10' },
    addon_requested: { Icon: TrendingUp, cls: 'text-teal-500', bg: 'bg-teal-500/10' },
    addon_applied: { Icon: CheckCircle2, cls: 'text-teal-500', bg: 'bg-teal-500/10' },
};

const TABS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'requests', label: 'Requests' },
    { key: 'system', label: 'System Alerts' },
];

const SYSTEM_TYPES = ['trial_expiring_soon', 'grace_period_started', 'subscription_expired', 'plan_upgraded'];

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function SuperAdminNotificationsPage() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [tab, setTab] = useState('all');
    const [loading, setLoading] = useState(true);
    const [markingAll, setMarkingAll] = useState(false);

    const fetchNotifications = useCallback(() => {
        getNotifications()
            .then(res => {
                setNotifications(res.data || []);
                setUnreadCount(res.unreadCount || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

    const handleMarkRead = async (n) => {
        if (n.isRead) return;
        try {
            await markNotificationRead(n._id);
            setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, isRead: true } : x));
            setUnreadCount(c => Math.max(0, c - 1));
        } catch {
            toast.error('Failed to mark as read.');
        }
    };

    const handleMarkAll = async () => {
        setMarkingAll(true);
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(x => ({ ...x, isRead: true })));
            setUnreadCount(0);
            toast.success('All notifications marked as read.');
        } catch {
            toast.error('Failed to mark all as read.');
        } finally {
            setMarkingAll(false);
        }
    };

    const filtered = notifications.filter(n => {
        if (tab === 'unread') return !n.isRead;
        if (tab === 'requests') return n.type === 'upgrade_requested' || n.type === 'addon_requested';
        if (tab === 'system') return SYSTEM_TYPES.includes(n.type);
        return true;
    });

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="mx-auto  space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
                    <p className="text-sm text-muted-foreground">
                        {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={handleMarkAll}
                        disabled={markingAll}
                    >
                        <Check className="h-4 w-4" />
                        Mark all read
                    </Button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 rounded-lg border border-border bg-muted/30 p-1">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            tab === t.key
                                ? 'bg-card text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Notification Feed */}
            <div className="space-y-2">
                {filtered.length === 0 ? (
                    <div className="flex h-40 items-center justify-center rounded-xl border border-border bg-card">
                        <p className="text-sm text-muted-foreground">No notifications in this category.</p>
                    </div>
                ) : (
                    filtered.map(n => {
                        const ti = TYPE_ICON[n.type] || TYPE_ICON.upgrade_requested;
                        const Icon = ti.Icon;
                        return (
                            <div
                                key={n._id}
                                className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${
                                    n.isRead
                                        ? 'border-border bg-card opacity-70'
                                        : 'border-border bg-card'
                                }`}
                            >
                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${ti.bg}`}>
                                    <Icon className={`h-4 w-4 ${ti.cls}`} />
                                </div>
                                <div className="min-w-0 flex-1 space-y-0.5">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-semibold text-foreground">{n.title}</p>
                                        <div className="flex shrink-0 items-center gap-2">
                                            {!n.isRead && (
                                                <span className="h-2 w-2 rounded-full bg-primary" />
                                            )}
                                            <span className="text-xs text-muted-foreground">{timeAgo(n.createdAt)}</span>
                                        </div>
                                    </div>
                                    {n.companyName && (
                                        <p className="text-xs font-medium text-muted-foreground">{n.companyName}</p>
                                    )}
                                    <p className="text-sm text-muted-foreground">{n.message}</p>
                                    <div className="flex items-center gap-2 pt-1">
                                        {n.data?.companyId && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 gap-1 text-xs"
                                                onClick={() => navigate(`/super-admin/companies/${n.data.companyId}`)}
                                            >
                                                View Company
                                                <ExternalLink className="h-3 w-3" />
                                            </Button>
                                        )}
                                        {!n.isRead && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 text-xs text-muted-foreground"
                                                onClick={() => handleMarkRead(n)}
                                            >
                                                Mark read
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
